import Button from '../../../../../components/Button/Button';
import DownloadFileLink from '../../../../../components/DownloadFileLink/DownloadFileLink';
import DocsInputNew from '../../../../../components/Inputs/DocsInput/DocsInputNew';
import { colors } from '../../../../../constants/colors';
import { FLOW_ECP, FLOW_PRINT, displayPhone, formatWhen, personName } from './form';
import { card, hint, title } from './parts';

const ROLE_LABEL = { seller: 'Продавец', buyer: 'Покупатель', agent: 'Агент' };

// Название площадки показываем как есть: ОПФ бывает не только ТОО, а в
// договор идёт то же юрлицо, что и в этом списке.
const agentCompany = (raw) => String(raw || '').trim() || 'Агент';

const TextAction = ({ disabled, onClick, children }) => (
  <span
    role="button"
    tabIndex={disabled ? -1 : 0}
    onClick={disabled ? undefined : onClick}
    onKeyDown={(event) => {
      if (disabled) return;
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onClick();
      }
    }}
    style={{
      color: disabled ? colors.dimGray : colors.cameo,
      cursor: disabled ? 'default' : 'pointer',
      textDecoration: 'underline',
      fontSize: 14,
      whiteSpace: 'nowrap',
    }}
  >
    {children}
  </span>
);

const ecpPartyPhone = (state, form, role) => {
  if (role === 'agent') return form.agent?.phone || state?.parties?.agent?.phone;
  return form[role]?.phone || state?.parties?.[role]?.phone || state?.values?.[role]?.phone;
};

const Document = ({
  file,
  state,
  form,
  flow,
  busy,
  sendingSms,
  signedFile,
  setSignedFile,
  uploading,
  onSendEcp,
  onRestartEcp,
  onUploadSigned,
}) => {
  if (!file?.url) return null;
  const ecp = state?.ecp || {};
  const signers = ecp.signers || [];
  const ecpStarted = signers.some((s) => s.status === 'sms' || s.status === 'signed');

  return (
    <div style={{ ...card, background: '#fff' }}>
      <p style={title}>Документ</p>
      <DownloadFileLink
        document={{
          ...file,
          url: ecp.currentUrl || file.url,
          type: { title: ecp.signedCount ? `Договор · ${ecp.signedCount} из 3 подписей` : 'Трёхсторонний договор' },
        }}
      />
      {ecp.unsignedUrl && ecp.unsignedUrl !== (ecp.currentUrl || file.url) && (
        <a href={ecp.unsignedUrl} target="_blank" rel="noreferrer" style={{ ...hint, color: colors.cameo }}>
          Файл без подписей
        </a>
      )}
      {flow === FLOW_ECP && (
        <div style={{ marginTop: 8 }}>
          {ecp.stage === 'complete' ? <p style={hint}>Подписан.</p> : null}
          {['seller', 'buyer', 'agent'].map((role, index) => {
            const signer = signers.find((s) => s.role === role) || {};
            const name =
              signer.name ||
              (role === 'agent' ? form.agent?.director : personName(form[role])) ||
              '—';
            const phone = signer.phone || ecpPartyPhone(state, form, role);
            const isNext = ecp.nextRole === role && ecp.stage !== 'complete';
            const waiting = signer.status === 'sms';
            const signed = signer.status === 'signed';
            const heading =
              role === 'agent' ? `${ROLE_LABEL[role]} · ${agentCompany(state?.values?.agent?.name)}` : `${index + 1}. ${ROLE_LABEL[role]}`;
            const who =
              role === 'agent' && name && name !== '—'
                ? `${name}, представитель`
                : name;
            return (
              <div
                key={role}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  padding: '12px 0',
                  borderTop: '1px solid #eee',
                }}
              >
                <div>
                  <p style={{ fontWeight: 600 }}>
                    {role === 'agent' ? `3. ${heading}` : heading}
                  </p>
                  <p style={{ ...hint, margin: 0 }}>
                    {who}
                    {signed && signer.signedAt ? ` · подписано ${formatWhen(signer.signedAt)}` : ''}
                    {waiting && signer.smsAt ? ` · отправлено ${formatWhen(signer.smsAt)}` : ''}
                    {!signed && !waiting && phone ? ` · ${displayPhone(phone)}` : ''}
                    {!signed && !waiting && !phone ? ' · нет телефона' : ''}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'flex-end', alignItems: 'center' }}>
                  {signed && (signer.copyUrl || signer.signedPdfUrl) && (
                    <TextAction
                      onClick={() => window.open(signer.copyUrl || signer.signedPdfUrl, '_blank', 'noopener,noreferrer')}
                    >
                      Открыть документ
                    </TextAction>
                  )}
                  {waiting && signer.link && (
                    <TextAction onClick={() => window.open(signer.link, '_blank', 'noopener,noreferrer')}>
                      Открыть документ
                    </TextAction>
                  )}
                  {isNext && waiting && (
                    <TextAction disabled={busy || sendingSms === role} onClick={() => onSendEcp(role)}>
                      {sendingSms === role ? 'Отправляем…' : 'Отправить СМС повторно'}
                    </TextAction>
                  )}
                  {isNext && !waiting && (
                    <Button
                      disabled={busy || !phone}
                      text={sendingSms === role ? 'Отправляем…' : 'Отправить СМС'}
                      fill
                      onClick={() => onSendEcp(role)}
                    />
                  )}
                </div>
              </div>
            );
          })}
          {ecpStarted && (
            <p style={{ ...hint, margin: '12px 0 0' }}>
              <TextAction disabled={busy} onClick={onRestartEcp}>
                Начать ЭЦП заново
              </TextAction>
            </p>
          )}
        </div>
      )}
      {flow === FLOW_PRINT &&
        (state.signedDocument ? (
          <p style={{ ...hint, color: '#1E6B2E', marginTop: 12 }}>
            Скан загружен ·{' '}
            <a href={state.signedDocument.url} target="_blank" rel="noreferrer" style={{ color: colors.cameo }}>
              открыть
            </a>
          </p>
        ) : (
          <div style={{ marginTop: 16 }}>
            <DocsInputNew
              id="tripartite-signed"
              text="Скан (необязательно)"
              label={signedFile?.name || 'PDF или фото'}
              value={signedFile}
              onChange={setSignedFile}
              accept="application/pdf,image/*"
            />
            <div style={{ marginTop: 12 }}>
              <Button disabled={uploading || !signedFile} text="Загрузить скан" onClick={onUploadSigned} />
            </div>
          </div>
        ))}
    </div>
  );
};

export default Document;
export { ROLE_LABEL };
